/**
 * IncidentReportPage — Generic incident reporting
 *
 * Used by: CFSS, ALF, PCA, construction, legal, etc.
 * Industry config comes from constellation/settings.
 *
 * Route: /{industry}/incidents
 * Scope: incident:report
 */

import React, { useState, useCallback } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Container,
  Group,
  Loader,
  Modal,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Tabs,
  Text,
  Textarea,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCheck,
  IconClock,
  IconPlus,
  IconAlertCircle,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useQuery, useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useHasScope } from '@/auth';

const GET_INCIDENTS = gql`
  query GetIncidents($status: String, $limit: Int) {
    incidents(status: $status, limit: $limit) {
      id
      type
      severity
      description
      status
      reportedBy
      reportedAt
      resolvedAt
    }
  }
`;

const CREATE_INCIDENT = gql`
  mutation CreateIncident($input: CreateIncidentInput!) {
    createIncident(input: $input) {
      id
      type
      status
    }
  }
`;

interface Incident {
  id: string;
  type: string;
  severity: string;
  description: string;
  status: string;
  reportedBy: string | null;
  reportedAt: string;
  resolvedAt: string | null;
}

interface IncidentType {
  value: string;
  label: string;
}

interface IncidentReportPageProps {
  title?: string;
  subtitle?: string;
  incidentTypes?: IncidentType[];
  scope?: string;
}

const DEFAULT_INCIDENT_TYPES: IncidentType[] = [
  { value: 'injury', label: 'Injury' },
  { value: 'fall', label: 'Fall' },
  { value: 'safety', label: 'Safety Concern' },
  { value: 'equipment', label: 'Equipment Issue' },
  { value: 'behavioral', label: 'Behavioral Concern' },
  { value: 'other', label: 'Other' },
];

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function getSeverityColor(severity: string): string {
  switch (severity.toLowerCase()) {
    case 'critical': return 'red';
    case 'high': return 'orange';
    case 'medium': return 'yellow';
    case 'low': return 'blue';
    default: return 'gray';
  }
}

function getStatusColor(status: string): string {
  switch (status.toLowerCase()) {
    case 'open': return 'red';
    case 'investigating': return 'orange';
    case 'resolved': return 'green';
    case 'closed': return 'gray';
    default: return 'gray';
  }
}

export function IncidentReportPage({
  title = 'Incident Reports',
  subtitle = 'Report and track safety concerns',
  incidentTypes = DEFAULT_INCIDENT_TYPES,
  scope = 'incident:report',
}: IncidentReportPageProps) {
  const [activeTab, setActiveTab] = useState<string | null>('open');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newIncident, setNewIncident] = useState({ type: '', severity: 'medium', description: '' });

  const canReport = useHasScope(scope);

  const { data: openData, loading: openLoading, refetch } = useQuery<{ incidents: Incident[] }>(
    GET_INCIDENTS,
    { variables: { status: 'open', limit: 100 } }
  );

  const { data: resolvedData, loading: resolvedLoading } = useQuery<{ incidents: Incident[] }>(
    GET_INCIDENTS,
    { variables: { status: 'resolved', limit: 50 } }
  );

  const [createIncident, { loading: creating }] = useMutation(CREATE_INCIDENT);

  const loading = openLoading || resolvedLoading;
  const openIncidents = openData?.incidents ?? [];
  const resolvedIncidents = resolvedData?.incidents ?? [];

  const handleCreate = useCallback(async () => {
    if (!newIncident.type || !newIncident.description) return;
    try {
      await createIncident({
        variables: { input: newIncident },
      });
      notifications.show({
        title: 'Incident Reported',
        message: 'The incident has been logged and will be reviewed.',
        color: 'green',
      });
      setCreateModalOpen(false);
      setNewIncident({ type: '', severity: 'medium', description: '' });
      refetch();
    } catch (err) {
      notifications.show({
        title: 'Report Failed',
        message: err instanceof Error ? err.message : 'An error occurred.',
        color: 'red',
      });
    }
  }, [newIncident, createIncident, refetch]);

  if (loading) {
    return <Center h="60vh"><Loader size="lg" /></Center>;
  }

  if (!canReport) {
    return (
      <Container size="md" py="xl">
        <Alert color="red" title="Access Denied" icon={<IconAlertCircle />}>
          You do not have permission to report incidents.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start">
          <Group gap="xs">
            <ThemeIcon size="lg" variant="light" color="red">
              <IconAlertTriangle size={20} />
            </ThemeIcon>
            <Stack gap={0}>
              <Title order={2}>{title}</Title>
              <Text size="sm" c="dimmed">{subtitle}</Text>
            </Stack>
          </Group>
          <Button leftSection={<IconPlus size={16} />} color="red" onClick={() => setCreateModalOpen(true)}>
            Report Incident
          </Button>
        </Group>

        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Open</Text>
              <ThemeIcon size="sm" variant="light" color="red"><IconAlertTriangle size={14} /></ThemeIcon>
            </Group>
            <Text size="xl" fw={700} c={openIncidents.length > 0 ? 'red' : undefined}>{openIncidents.length}</Text>
          </Card>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Resolved</Text>
              <ThemeIcon size="sm" variant="light" color="green"><IconCheck size={14} /></ThemeIcon>
            </Group>
            <Text size="xl" fw={700} c="green">{resolvedIncidents.length}</Text>
          </Card>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Critical</Text>
              <ThemeIcon size="sm" variant="light" color="red"><IconAlertCircle size={14} /></ThemeIcon>
            </Group>
            <Text size="xl" fw={700} c={openIncidents.filter(i => i.severity === 'critical').length > 0 ? 'red' : undefined}>
              {openIncidents.filter(i => i.severity === 'critical').length}
            </Text>
          </Card>
        </SimpleGrid>

        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="open" leftSection={<IconClock size={14} />}>Open ({openIncidents.length})</Tabs.Tab>
            <Tabs.Tab value="resolved" leftSection={<IconCheck size={14} />}>Resolved ({resolvedIncidents.length})</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="open" pt="md">
            {openIncidents.length === 0 ? (
              <Alert color="green" title="No Open Incidents" icon={<IconCheck />}>No incidents require attention.</Alert>
            ) : (
              <Paper withBorder>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Type</Table.Th>
                      <Table.Th>Severity</Table.Th>
                      <Table.Th>Description</Table.Th>
                      <Table.Th>Reported</Table.Th>
                      <Table.Th>Status</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {openIncidents.map(incident => (
                      <Table.Tr key={incident.id}>
                        <Table.Td><Text size="sm" fw={500}>{incident.type}</Text></Table.Td>
                        <Table.Td><Badge color={getSeverityColor(incident.severity)} size="sm">{incident.severity}</Badge></Table.Td>
                        <Table.Td><Text size="sm" lineClamp={2}>{incident.description}</Text></Table.Td>
                        <Table.Td><Text size="sm" c="dimmed">{formatDate(incident.reportedAt)}</Text></Table.Td>
                        <Table.Td><Badge color={getStatusColor(incident.status)} size="sm">{incident.status}</Badge></Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Paper>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="resolved" pt="md">
            {resolvedIncidents.length === 0 ? (
              <Alert color="gray" title="No Resolved Incidents" icon={<IconClock />}>No incidents have been resolved yet.</Alert>
            ) : (
              <Paper withBorder>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Type</Table.Th>
                      <Table.Th>Severity</Table.Th>
                      <Table.Th>Reported</Table.Th>
                      <Table.Th>Resolved</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {resolvedIncidents.map(incident => (
                      <Table.Tr key={incident.id}>
                        <Table.Td><Text size="sm" fw={500}>{incident.type}</Text></Table.Td>
                        <Table.Td><Badge color={getSeverityColor(incident.severity)} size="sm">{incident.severity}</Badge></Table.Td>
                        <Table.Td><Text size="sm" c="dimmed">{formatDate(incident.reportedAt)}</Text></Table.Td>
                        <Table.Td><Text size="sm" c="dimmed">{formatDate(incident.resolvedAt)}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Paper>
            )}
          </Tabs.Panel>
        </Tabs>
      </Stack>

      <Modal opened={createModalOpen} onClose={() => setCreateModalOpen(false)} title="Report Incident" size="md">
        <Stack gap="md">
          <Select
            label="Incident Type"
            placeholder="Select type"
            required
            data={incidentTypes}
            value={newIncident.type}
            onChange={v => setNewIncident(prev => ({ ...prev, type: v ?? '' }))}
          />
          <Select
            label="Severity"
            required
            data={[
              { value: 'low', label: 'Low - Minor concern' },
              { value: 'medium', label: 'Medium - Needs attention' },
              { value: 'high', label: 'High - Urgent' },
              { value: 'critical', label: 'Critical - Immediate action' },
            ]}
            value={newIncident.severity}
            onChange={v => setNewIncident(prev => ({ ...prev, severity: v ?? 'medium' }))}
          />
          <Textarea
            label="Description"
            placeholder="Describe what happened..."
            required
            minRows={4}
            value={newIncident.description}
            onChange={e => setNewIncident(prev => ({ ...prev, description: e.currentTarget.value }))}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setCreateModalOpen(false)} disabled={creating}>Cancel</Button>
            <Button color="red" onClick={handleCreate} loading={creating} disabled={!newIncident.type || !newIncident.description}>
              Submit Report
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

export default IncidentReportPage;
